import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useIsMobile } from "../shared"
import { TableWrap, Btn, Badge, Avatar } from "../shared/components"
import {
  getSession, listAttendees, getRsvp, endSession, markAttendanceBulk, removeAttendance,
  startZoom, retryZoomMeeting, syncZoom, getZoomParticipants,
} from "../api/sessions.api"
import { listEnrollments, listClassEnrollments } from "../api/programs.api"
import { bukaTautan } from "../lib/bukaTautan"

function fmtDateTime(date, lang) {
  if (!date) return "—"
  return new Date(date).toLocaleString(lang === "id" ? "id-ID" : "en-US", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })
}

function typeLabel(type, lang) {
  if (type === "LECTURE") return lang === "id" ? "Kuliah" : "Lecture"
  if (type === "DRILL") return "Drill"
  if (type === "EXAM") return lang === "id" ? "Ujian" : "Exam"
  return null
}

function DetailRow({ label, value }) {
  return (
    <div className="row between">
      <span className="small muted">{label}</span>
      <span className="small" style={{ fontWeight: 500, textAlign: "right" }}>{value}</span>
    </div>
  )
}

const menitDari = (detik) => Math.round((detik || 0) / 60)

function labelHasilZoom(outcome, lang) {
  const id = lang === "id"
  if (outcome === "PRESENT") return { tone: "success", teks: id ? "Hadir" : "Present" }
  if (outcome === "BELOW_THRESHOLD") return { tone: "warning", teks: id ? "Di bawah batas" : "Below threshold" }
  if (outcome === "NOT_ENROLLED") return { tone: undefined, teks: id ? "Tidak terdaftar" : "Not enrolled" }
  return { tone: "warning", teks: id ? "Tidak cocok" : "Unmatched" }
}

// Meeting otomatis: tautan host, status penarikan laporan, dan daftar tinjauan.
// Yang di bawah batas atau tidak cocok tidak menjadi kehadiran, jadi staf perlu
// melihatnya di sini lalu memakai "Tandai Hadir" bila memang hadir.
function ZoomPanel({ session, lang, onChanged }) {
  const id = lang === "id"
  const [data, setData] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const dikelola = Boolean(session.zoom_meeting_id)
  const gagalDibuat = !dikelola && session.conference_mode !== "ONSITE" && !session.conference_url

  const muat = React.useCallback(() => {
    if (!dikelola) return
    getZoomParticipants(session.id).then(setData).catch(() => setData(null))
  }, [dikelola, session.id])
  React.useEffect(() => { muat() }, [muat])

  const galat = (e) => toast.error(e?.response?.data?.error || e.message)

  const mulai = async () => {
    try {
      await bukaTautan(() => startZoom(session.id).then((r) => r.startUrl))
      onChanged()
    } catch (e) { galat(e) }
  }

  const coba = async () => {
    setBusy(true)
    try {
      await retryZoomMeeting(session.id)
      toast.success(id ? "Meeting Zoom dibuat" : "Zoom meeting created")
      onChanged()
    } catch (e) { galat(e) } finally { setBusy(false) }
  }

  const sinkron = async () => {
    const pesan = id
      ? "Tarik ulang laporan Zoom?\n\nKehadiran yang pernah dibatalkan dapat tercatat lagi bila pesertanya mencapai batas hadir."
      : "Fetch the Zoom report again?\n\nAttendance you removed earlier can be recorded again if the participant reached the threshold."
    if (!window.confirm(pesan)) return
    setBusy(true)
    try {
      const r = await syncZoom(session.id)
      if (r.status === "DONE") {
        toast.success(id ? `${r.recorded} kehadiran baru dicatat` : `${r.recorded} new attendance records`)
      } else {
        toast.error(r.error)
      }
      muat()
      onChanged()
    } catch (e) { galat(e) } finally { setBusy(false) }
  }

  if (gagalDibuat) {
    return (
      <div className="card mt-4">
        <div className="card-body row between" style={{ alignItems: "center", gap: 16 }}>
          <span className="small muted">
            {id ? "Sesi ini belum punya tautan meeting." : "This session has no meeting link yet."}
          </span>
          <Btn variant="secondary" size="sm" disabled={busy} onClick={coba}>
            {id ? "Buat meeting Zoom" : "Create Zoom meeting"}
          </Btn>
        </div>
      </div>
    )
  }
  if (!dikelola) return null

  const sudahMulai = new Date(session.scheduled_at) <= new Date()
  const status = data?.status
  const teksStatus = status === "DONE"
    ? (id ? `Laporan diambil ${fmtDateTime(data.syncedAt, lang)}` : `Report fetched ${fmtDateTime(data.syncedAt, lang)}`)
    : status === "FAILED"
      ? (id ? "Laporan gagal diambil." : "The report could not be fetched.")
      : (id
        ? "Kehadiran masuk otomatis setelah meeting berakhir. Sampai saat itu, persentase peserta bisa tampak turun."
        : "Attendance arrives automatically after the meeting ends. Until then, participant percentages may look lower.")

  return (
    <div className="card mt-4">
      <div className="card-header">
        <div className="h3">Zoom</div>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          {sudahMulai && (
            <Btn variant="ghost" size="sm" disabled={busy} onClick={sinkron}>
              {id ? "Sinkronkan ulang" : "Sync again"}
            </Btn>
          )}
          <Btn variant="primary" size="sm" icon={<Icon name="video" size={12} />} onClick={mulai}>
            {id ? "Mulai sebagai host" : "Start as host"}
          </Btn>
        </div>
      </div>
      <div className="col gap-1" style={{ padding: 16 }}>
        <div className="small muted">{teksStatus}</div>
        {data?.error && <div className="small" style={{ color: "var(--amber-50)" }}>{data.error}</div>}
      </div>
      {data?.participants?.length > 0 && (
        <TableWrap>
          <table className="tbl">
            <thead><tr>
              <th>{id ? "Peserta Zoom" : "Zoom participant"}</th>
              <th>{id ? "Lama" : "Duration"}</th>
              <th>{id ? "Hasil" : "Result"}</th>
            </tr></thead>
            <tbody>
              {data.participants.map((p) => {
                const hasil = labelHasilZoom(p.outcome, lang)
                return (
                  <tr key={p.id}>
                    <td>
                      <div className="small" style={{ fontWeight: 500 }}>{p.user?.full_name || p.name || "—"}</div>
                      <div className="tiny muted">{p.email || "—"}</div>
                    </td>
                    <td className="mono small">{menitDari(p.seconds)} {id ? "mnt" : "min"}</td>
                    <td><Badge tone={hasil.tone} dot>{hasil.teks}</Badge></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  )
}

export default function SessionDetailScreen() {
  const { lang, role, navigate, pageState } = useApp()
  const isMobile = useIsMobile()
  const sessionId = pageState?.sessionId
  const isStaff = role === "trainer" || role === "admin"
  const [session, setSession] = React.useState(null)
  const [attendees, setAttendees] = React.useState([])
  const [rsvp, setRsvp] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState(null)
  const [marking, setMarking] = React.useState(false) // dialog tandai hadir terbuka

  const reload = React.useCallback(() => {
    if (!sessionId) { setLoading(false); return }
    setLoading(true)
    Promise.all([getSession(sessionId), listAttendees(sessionId), getRsvp(sessionId)])
      .then(([s, a, r]) => { setSession(s); setAttendees(a); setRsvp(r) })
      .catch((e) => setErr(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [sessionId])

  React.useEffect(() => { reload() }, [reload])

  const handleEnd = async () => {
    try {
      await endSession(sessionId)
      toast.success(lang === "id" ? "Sesi diakhiri" : "Session ended")
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  // Membatalkan kehadiran menurunkan persentase tapi TIDAK mencabut status
  // layak sertifikat yang sudah menyala — itu tindakan terpisah. Dikatakan di
  // konfirmasi supaya staf tidak mengira ini otomatis membatalkan sertifikat.
  const handleRemove = async (a) => {
    const nama = a.user?.full_name || "peserta ini"
    const konfirmasi = lang === "id"
      ? `Batalkan kehadiran ${nama} pada sesi ini?\n\nPersentase kehadirannya akan turun, dan dia akan diberi tahu. Status "layak sertifikat" yang sudah menyala TIDAK ikut tercabut.`
      : `Remove ${nama}'s attendance for this session?\n\nTheir attendance percentage will drop and they will be notified. An already-granted "certificate eligible" status is NOT revoked.`
    if (!window.confirm(konfirmasi)) return
    try {
      await removeAttendance(sessionId, a.user.id)
      toast.success(lang === "id" ? "Kehadiran dibatalkan" : "Attendance removed")
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  if (loading) {
    return (
      <div className="main-inner">
        <p className="muted">{lang === "id" ? "Memuat..." : "Loading..."}</p>
      </div>
    )
  }

  if (err || !session) {
    return (
      <div className="main-inner">
        <Btn variant="ghost" size="sm" icon={<Icon name="chevron_left" size={14}/>} onClick={() => navigate("training")}>
          {lang === "id" ? "Kembali" : "Back"}
        </Btn>
        <div className="card card-body mt-4" style={{ textAlign: "center" }}>
          <p className="muted">{err || (lang === "id" ? "Sesi tidak ditemukan." : "Session not found.")}</p>
        </div>
      </div>
    )
  }

  const flaggedCount = attendees.filter((a) => a.is_flagged).length
  const hasGeo =
    session.location_lat != null &&
    session.location_lng != null &&
    session.geo_radius_m != null

  return (
    <div className="main-inner">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            <Btn variant="ghost" size="sm" icon={<Icon name="chevron_left" size={12}/>} onClick={() => navigate("training")} style={{ marginRight: 4, padding: "2px 8px", height: "auto" }}>
              {lang === "id" ? "Pelatihan" : "Training"}
            </Btn>
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>{session.title}</h1>
          <div className="row gap-2 mt-1">
            {typeLabel(session.session_type, lang) && (
              <Badge>{typeLabel(session.session_type, lang)}</Badge>
            )}
            {session.is_active && (
              <Badge tone="success" dot>{lang === "id" ? "Aktif" : "Live"}</Badge>
            )}
          </div>
        </div>
        <div className="page-header-actions">
          {session.is_active && (
            <Btn variant="ghost" size="sm" icon={<Icon name="close" size={12} />} onClick={handleEnd}>
              {lang === "id" ? "Akhiri Sesi" : "End Session"}
            </Btn>
          )}
          <Btn variant="primary" icon={<Icon name="qrCode" size={12} />} onClick={() => navigate("qr", { sessionId })}>
            {lang === "id" ? "Absensi" : "Attendance"}
          </Btn>
        </div>
      </div>

      <div className="grid mt-4" style={{ gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 16 }}>
        {/* Detail */}
        <div className="card">
          <div className="card-header"><div className="h3">{lang === "id" ? "Detail Sesi" : "Session Detail"}</div></div>
          <div className="col gap-3" style={{ padding: 16 }}>
            <DetailRow label={lang === "id" ? "Jadwal" : "Schedule"} value={fmtDateTime(session.scheduled_at, lang)} />
            <DetailRow
              label={lang === "id" ? "Durasi" : "Duration"}
              value={session.duration_min ? `${session.duration_min} ${lang === "id" ? "menit" : "min"}` : "—"}
            />
            <DetailRow label={lang === "id" ? "Lokasi" : "Location"} value={session.location || "—"} />
            <DetailRow label="Trainer" value={session.trainer?.full_name || "—"} />
            <DetailRow
              label="Geo-fence"
              value={hasGeo
                ? `${session.location_lat}, ${session.location_lng} · ${session.geo_radius_m} m`
                : (lang === "id" ? "Tidak diatur" : "Not set")}
            />
            <DetailRow
              label={lang === "id" ? "Berulang" : "Recurring"}
              value={session.series_id
                ? (lang === "id" ? "Ya — bagian rangkaian" : "Yes — part of a series")
                : (lang === "id" ? "Tidak" : "No")}
            />
            <DetailRow
              label={lang === "id" ? "Kapasitas" : "Capacity"}
              value={session.capacity != null ? String(session.capacity) : "—"}
            />
            <DetailRow
              label={lang === "id" ? "RSVP akan hadir" : "RSVP going"}
              value={rsvp
                ? `${rsvp.going}${session.capacity != null ? ` / ${session.capacity}` : ""}`
                : "—"}
            />
          </div>
        </div>

        {/* Attendance summary */}
        <div className="card">
          <div className="card-header"><div className="h3">{lang === "id" ? "Kehadiran" : "Attendance"}</div></div>
          <div className="col gap-4" style={{ padding: 16 }}>
            <div>
              <div className="stat-num">{attendees.length}</div>
              <div className="tiny muted">{lang === "id" ? "Total hadir" : "Total scanned"}</div>
            </div>
            <div>
              <div className="stat-num" style={{ color: flaggedCount ? "var(--amber-50)" : undefined }}>
                {flaggedCount}
              </div>
              <div className="tiny muted">{lang === "id" ? "Ditandai (anomali)" : "Flagged (anomaly)"}</div>
            </div>
          </div>
        </div>
      </div>

      {isStaff && <ZoomPanel session={session} lang={lang} onChanged={reload} />}

      {/* Attendees */}
      <div className="card mt-4">
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Daftar Hadir" : "Attendees"}</div>
          <div className="row gap-2" style={{ alignItems: "center" }}>
            <span className="card-header-sub">{attendees.length}</span>
            {isStaff && (
              <Btn variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => setMarking(true)}>
                {lang === "id" ? "Tandai Hadir" : "Mark Present"}
              </Btn>
            )}
          </div>
        </div>
        {attendees.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center" }} className="muted small">
            {lang === "id" ? "Belum ada yang absen." : "No attendance scans yet."}
          </div>
        ) : (
          <TableWrap>
          <table className="tbl">
            <thead><tr>
              <th>{lang === "id" ? "Peserta" : "Participant"}</th>
              <th>{lang === "id" ? "Waktu Scan" : "Scanned At"}</th>
              <th>Status</th>
              {isStaff && <th style={{ width: 44 }}></th>}
            </tr></thead>
            <tbody>
              {attendees.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className="row gap-2">
                      <Avatar name={a.user?.full_name || "?"} size={28} />
                      <div>
                        <div className="small" style={{ fontWeight: 500 }}>{a.user?.full_name || "—"}</div>
                        <div className="tiny muted">{a.user?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="mono small">{fmtDateTime(a.scanned_at, lang)}</td>
                  <td>
                    <div className="row gap-1" style={{ flexWrap: "wrap" }}>
                      {a.is_flagged ? (
                        <Badge tone="warning" dot title={a.flag_reason || ""}>{lang === "id" ? "Ditandai" : "Flagged"}</Badge>
                      ) : (
                        <Badge tone="success" dot>{lang === "id" ? "Sah" : "OK"}</Badge>
                      )}
                      {/* Kehadiran manual tidak boleh tersamar sebagai pemindaian
                          terverifikasi — itu yang menjaga arti daftar ini. */}
                      {a.recorded_by_id && (
                        <Badge title={[
                          lang === "id" ? `Dicatat oleh ${a.recorded_by?.full_name || "staf"}` : `Recorded by ${a.recorded_by?.full_name || "staff"}`,
                          a.note,
                        ].filter(Boolean).join(" · ")}>
                          Manual
                        </Badge>
                      )}
                      {a.zoom_seconds != null && (
                        <Badge title={lang === "id" ? "Dari laporan peserta Zoom" : "From the Zoom participant report"}>
                          Zoom · {menitDari(a.zoom_seconds)} {lang === "id" ? "mnt" : "min"}
                        </Badge>
                      )}
                    </div>
                  </td>
                  {isStaff && (
                    <td>
                      <Btn
                        variant="ghost"
                        size="sm"
                        title={lang === "id" ? "Batalkan kehadiran" : "Remove attendance"}
                        onClick={() => handleRemove(a)}
                      >
                        <Icon name="trash" size={13} />
                      </Btn>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </TableWrap>
        )}
      </div>

      {marking && (
        <MarkAttendanceModal
          session={session}
          sudahHadir={attendees.map((a) => a.user?.id).filter(Boolean)}
          lang={lang}
          onClose={() => setMarking(false)}
          onSaved={() => { setMarking(false); reload() }}
        />
      )}
    </div>
  )
}

// Staf menandai peserta hadir tanpa pemindaian QR — jalan keluar saat QR
// gagal, ponsel peserta mati, atau absensi dilakukan di kertas. Kehadiran
// ≥80% adalah syarat sertifikat, jadi masalah teknis tidak boleh membuat
// peserta yang benar-benar hadir kehilangan haknya.
//
// Daftarnya dikunci ke KELAS sesi ini. Backend hanya menerima peserta kelas
// sesi (findEnrollmentForClass), jadi peserta kelas lain yang dulu ikut tampil
// hanya berakhir sebagai galat saat disimpan. Sesi tanpa kelas hanya menerima
// enrolmen yang juga belum berkelas.
function MarkAttendanceModal({ session, sudahHadir, lang, onClose, onSaved }) {
  const sessionId = session.id
  const programId = session.program_id
  const classId = session.class_id
  const [peserta, setPeserta] = React.useState(null) // null = masih dimuat
  const [jumlahKelas, setJumlahKelas] = React.useState(0) // sebelum dikurangi yang sudah hadir
  const [dipilih, setDipilih] = React.useState(() => new Set()) // userId yang dicentang
  const [cari, setCari] = React.useState("")
  const [note, setNote] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!programId) return
    const muat = classId
      ? listClassEnrollments(programId, classId)
      : listEnrollments(programId).then((rows) => rows.filter((e) => !e.class_id))
    muat
      .then((rows) => {
        // Satu orang bisa punya dua baris enrolmen tanpa kelas; tampilkan sekali.
        const unik = new Map()
        for (const e of rows) if (e.user && !unik.has(e.user.id)) unik.set(e.user.id, e)
        setJumlahKelas(unik.size)
        setPeserta([...unik.values()].filter((e) => !sudahHadir.includes(e.user.id)))
      })
      .catch((e) => { toast.error(e?.response?.data?.error || e.message); setPeserta([]) })
    // sudahHadir sengaja tidak jadi dependency: dialognya dibuka sekali dengan
    // daftar yang sudah dibekukan saat itu, dan ditutup begitu tersimpan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, classId])

  const kataCari = cari.trim().toLowerCase()
  const terlihat = (peserta || []).filter(
    (e) =>
      !kataCari ||
      [e.user.full_name, e.user.email, e.client_company].some((v) => (v || "").toLowerCase().includes(kataCari))
  )
  const semuaTerlihatDipilih = terlihat.length > 0 && terlihat.every((e) => dipilih.has(e.user.id))

  function centang(id) {
    setDipilih((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // "Pilih semua" hanya berlaku untuk yang sedang terlihat setelah dicari —
  // pilihan di luar hasil pencarian tidak ikut tersentuh.
  function centangSemua() {
    setDipilih((prev) => {
      const next = new Set(prev)
      for (const e of terlihat) {
        if (semuaTerlihatDipilih) next.delete(e.user.id)
        else next.add(e.user.id)
      }
      return next
    })
  }

  async function simpan() {
    setBusy(true)
    try {
      const r = await markAttendanceBulk(sessionId, [...dipilih], note.trim() || undefined)
      const bagian = [lang === "id" ? `${r.created.length} dicatat hadir` : `${r.created.length} marked present`]
      if (r.already.length) {
        bagian.push(lang === "id" ? `${r.already.length} sudah hadir` : `${r.already.length} already present`)
      }
      if (r.notEnrolled.length) {
        bagian.push(lang === "id"
          ? `${r.notEnrolled.length} ditolak (bukan peserta kelas ini)`
          : `${r.notEnrolled.length} rejected (not in this class)`)
      }
      if (r.created.length) toast.success(bagian.join(" · "))
      else toast.error(bagian.join(" · "))
      onSaved()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "grid", placeItems: "center", zIndex: 50, padding: 16 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: "min(440px, 100%)", background: "var(--surface)" }}
      >
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Tandai hadir" : "Mark present"}</div>
          <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14} /></Btn>
        </div>
        <div style={{ padding: 20 }}>
          <div className="small muted" style={{ marginBottom: 14 }}>
            {lang === "id"
              ? "Untuk peserta yang hadir tapi tidak sempat memindai QR — mis. QR gagal, ponsel mati, atau absensi di kertas. Tercatat sebagai \"Manual\" beserta nama pencatatnya, dan pesertanya diberi tahu."
              : "For participants who attended but couldn't scan the QR — e.g. the QR failed, their phone died, or attendance was taken on paper. Recorded as \"Manual\" with your name, and the participant is notified."}
          </div>

          {/* Konteks yang tidak bisa diubah: daftar di bawah hanya berisi peserta
              kelas ini, jadi staf perlu tahu kelas mana yang sedang dicatat. */}
          <div
            className="small"
            style={{
              marginBottom: 14, padding: "8px 10px", lineHeight: 1.6,
              background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8,
            }}
          >
            <div>
              <span className="muted">Program: </span>
              <strong>{session.program?.name || "—"}</strong>
            </div>
            <div>
              <span className="muted">{lang === "id" ? "Kelas: " : "Class: "}</span>
              <strong>
                {session.training_class
                  ? session.training_class.name +
                    (session.training_class.code && session.training_class.code !== session.training_class.name
                      ? ` (${session.training_class.code})`
                      : "")
                  : (lang === "id" ? "Tanpa kelas" : "No class")}
              </strong>
            </div>
          </div>

          <label className="small" style={{ display: "block", marginBottom: 6 }}>
            {lang === "id" ? "Peserta" : "Participants"}
          </label>
          {peserta === null ? (
            <div className="small muted">{lang === "id" ? "Memuat peserta…" : "Loading participants…"}</div>
          ) : peserta.length === 0 ? (
            <div className="small muted">
              {jumlahKelas === 0
                ? (lang === "id" ? "Belum ada peserta di kelas ini." : "This class has no participants yet.")
                : (lang === "id" ? "Semua peserta kelas ini sudah tercatat hadir." : "Every participant in this class is already marked present.")}
            </div>
          ) : (
            <>
              <input
                className="input"
                type="search"
                value={cari}
                autoFocus
                placeholder={lang === "id" ? "Cari nama, email, atau perusahaan…" : "Search name, email, or company…"}
                onChange={(e) => setCari(e.target.value)}
                style={{ width: "100%", height: 36, marginBottom: 8 }}
              />
              <div className="row between" style={{ alignItems: "center", marginBottom: 6 }}>
                <label
                  className="row gap-2 small"
                  style={{ alignItems: "center", cursor: terlihat.length ? "pointer" : "default" }}
                >
                  <input
                    type="checkbox"
                    checked={semuaTerlihatDipilih}
                    disabled={!terlihat.length}
                    onChange={centangSemua}
                  />
                  {lang === "id" ? "Pilih semua" : "Select all"}
                  {kataCari && <span className="tiny muted">({terlihat.length})</span>}
                </label>
                <span className="tiny muted">
                  {lang === "id" ? `${dipilih.size} dipilih` : `${dipilih.size} selected`}
                </span>
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
                {terlihat.length === 0 ? (
                  <div className="small muted" style={{ padding: 12, textAlign: "center" }}>
                    {lang === "id" ? "Tidak ada peserta yang cocok." : "No matching participants."}
                  </div>
                ) : (
                  terlihat.map((e) => (
                    <label
                      key={e.user.id}
                      className="row gap-2"
                      style={{
                        alignItems: "flex-start", padding: "8px 10px", cursor: "pointer",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={dipilih.has(e.user.id)}
                        onChange={() => centang(e.user.id)}
                        style={{ marginTop: 3 }}
                      />
                      <span className="col" style={{ minWidth: 0 }}>
                        <span className="small" style={{ fontWeight: 500 }}>{e.user.full_name}</span>
                        <span className="tiny muted">
                          {e.user.email}{e.client_company ? ` · ${e.client_company}` : ""}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}

          <label className="small" style={{ display: "block", margin: "14px 0 6px" }}>
            {lang === "id" ? "Alasan (opsional, berlaku untuk semua yang dipilih)" : "Reason (optional, applies to everyone selected)"}
          </label>
          <input
            className="input"
            type="text"
            maxLength={200}
            value={note}
            placeholder={lang === "id" ? "mis. QR error di lokasi" : "e.g. QR failed on site"}
            onChange={(e) => setNote(e.target.value)}
            style={{ width: "100%", height: 36 }}
          />
        </div>
        <div className="card-header" style={{ borderTop: "1px solid var(--border)", borderBottom: "none" }}>
          <div />
          <div className="row gap-2">
            <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>
              {lang === "id" ? "Batal" : "Cancel"}
            </Btn>
            <Btn variant="primary" size="sm" onClick={simpan} disabled={busy || dipilih.size === 0}>
              {busy
                ? (lang === "id" ? "Menyimpan…" : "Saving…")
                : (lang === "id" ? `Tandai hadir (${dipilih.size})` : `Mark present (${dipilih.size})`)}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  )
}
